<script lang="ts">
  import { useClickOutside } from '$lib/utils/useClickOutside';
  import { fly } from 'svelte/transition';
  import { twMerge } from 'tailwind-merge';
  import Icon from '../Icon.svelte';
  import Spinner from '../Spinner.svelte';
  import Input from './Input.svelte';

  interface SelectOption {
    value: string | number;
    label: string | string[];
  }

  type SelectVariant = 'primary' | 'transparent';
  type SelectSize = 'sm' | 'md' | 'lg';

  interface Props {
    options?: SelectOption[];
    value?: string | number | null;
    variant?: SelectVariant;
    size?: SelectSize;
    label?: string;
    disabled?: boolean;
    searchable?: boolean;
    onChange?: (value: string | number | null) => void;
    placeholder?: string;
    valid?: boolean;
    icon?: boolean;
    loading?: boolean;
    dropdown?: boolean;
    name?: string;
    dataTestId?: string;
    class?: string | null;
    children?: import('svelte').Snippet;
    optionSnippet?: import('svelte').Snippet<[SelectOption]>;
  }

  let {
    options = [],
    value = $bindable(),
    variant = 'primary',
    size = 'md',
    label = '',
    disabled = false,
    searchable = false,
    onChange = () => undefined,
    placeholder = 'Välj',
    valid = true,
    icon = false,
    loading = false,
    dropdown = false,
    name = '',
    dataTestId = '',
    class: className = null,
    children = undefined,
    optionSnippet = undefined,
  }: Props = $props();

  let node: HTMLDivElement | undefined = $state();
  let selectElement: HTMLSelectElement | undefined = $state();
  let expanded = $state(false);

  // svelte-ignore state_referenced_locally
  let filteredOptions = $state(options);

  let dropDownContent: HTMLDivElement | undefined = $state();

  // Plain $effect: this measures the dropdown, so it needs the DOM in place.
  $effect(() => {
    if (!expanded || !dropDownContent) return;
    const dropdownRect = dropDownContent.getBoundingClientRect();
    const h = dropdownRect.height;
    const w = dropdownRect.width;
    const y = dropdownRect.top;
    const x = dropdownRect.left;
    const wh = window.innerHeight;
    const ww = window.innerWidth;
    const diffY = h + y - wh;
    const diffX = w + x - ww;

    // Reset any previous positioning
    dropDownContent.style.setProperty('top', '');
    dropDownContent.style.setProperty('left', '');
    dropDownContent.style.setProperty('bottom', '');

    // If there's not enough space below, show above
    if (diffY > 0) {
      dropDownContent.style.setProperty('top', 'auto');
      dropDownContent.style.setProperty('bottom', '100%');
      dropDownContent.style.setProperty('margin-bottom', '4px');
    }

    // Handle horizontal overflow
    if (diffX > 0) {
      dropDownContent.style.setProperty('left', `-${diffX + 16}px`);
    }
  });

  const toggleExpanded = () => {
    return !disabled && (expanded = !expanded);
  };

  const clearValue = () => !disabled && value && handleChange(null);

  let searchValue: string = $state('');

  const handleChange = (val: string | number | null) => {
    if (selectElement) selectElement.value = String(val ?? '');
    value = val;

    onChange(value);

    selectElement?.dispatchEvent(new Event('change'));
    expanded = false;
  };

  const variantClasses: Record<SelectVariant, string> = {
    primary: 'border-gray-6 text-gray-3 hover:border-white',
    transparent:
      'text-gray bg-transparent border-0 col-span-3 @apply focus:ring-0 focus:ring-offset-0;',
  };

  const sizeClasses: Record<SelectSize, string> = {
    sm: 'py-1 px-1 text-xs',
    md: 'py-2 px-2 text-md',
    lg: 'py-4 px-4 text-lg',
  };

  const classes = $derived(
    twMerge([
      'h-10 block border text-left w-full rounded-md outline-none transition focus:border-white',
      variantClasses[variant],
      sizeClasses[size],
      !valid &&
        'border-error hover:border-error dark:border-error dark:hover:border-error animate-shake',
      disabled && 'bg-gray-9 border-gray-9 hover:border-gray-9',
      value ? 'text-gray-3' : 'text-gray-6',
      icon && 'pl-8',
      className,
    ]),
  );

  const classesIcon = $derived(
    twMerge([
      variant !== 'transparent' &&
        'absolute flex rounded-sm  w-10 justify-center right-1 w-6 h-full items-center transition',
      expanded && !value && 'rotate-180',
      disabled && 'cursor-default hidden',
    ]),
  );

  const selectedOption = $derived(options.find((opt) => opt.value === value));

  // Was `$: !expanded && (searchValue = '')` — clear the search box on collapse.
  $effect(() => {
    if (!expanded) searchValue = '';
  });

  const filterOptions = () => {
    filteredOptions = options.filter((option) =>
      typeof option.label === 'string'
        ? option.label.toLowerCase().includes(searchValue.toLowerCase())
        : option.label.some((l) =>
            l.toLowerCase().includes(searchValue.toLowerCase()),
          ),
    );
  };
</script>

<!-- Select tag for use with FormData -->
<select bind:this={selectElement} class="hidden" {name} {value}>
  {#each options as option}
    <option value={option.value}>{option.label}</option>
  {/each}
</select>

<div class="form-select" bind:this={node}>
  <div>
    {#if label}
      <div class="mb-1 font-mono dark:text-white">{label}</div>
    {/if}
    <div
      class="relative flex w-full {!valid && 'animate-shake'}"
      use:useClickOutside
      onclickOutside={() => (expanded = false)}
    >
      {#if searchable}
        <Input
          {disabled}
          placeholder={(typeof selectedOption?.label === 'string'
            ? selectedOption.label
            : null) || placeholder}
          onfocus={toggleExpanded}
          bind:value={searchValue}
          oninput={filterOptions}
          {valid}
          class={twMerge([
            value && 'placeholder:text-gray-2',
            disabled && 'placeholder:text-gray-6',
            disabled && value && 'placeholder:text-gray-3',
          ])}
          {dataTestId}
        />
      {:else}
        <button
          type="button"
          {disabled}
          class={classes}
          onclick={toggleExpanded}
          data-testid={dataTestId}
        >
          {#if selectedOption && Array.isArray(selectedOption.label)}
            <div class="mr-6 flex justify-between">
              {#each selectedOption.label as lbl}
                <span>{lbl}</span>
              {/each}
            </div>
          {:else}
            <span class="self-center">
              {(typeof selectedOption?.label === 'string' &&
                selectedOption.label) ||
                placeholder}
            </span>
          {/if}
        </button>
      {/if}

      {#if expanded && !disabled}
        <div
          bind:this={dropDownContent}
          transition:fly={{ y: 4, duration: 150 }}
          class="absolute z-20 mt-8 flex max-h-56 w-full flex-col overflow-y-auto rounded-sm border bg-white dark:bg-gray-9"
        >
          {#each searchValue ? filteredOptions : options as option, index (index)}
            <button
              type="button"
              class="w-full p-2 text-left transition hover:bg-gray-8"
              onclick={() => handleChange(option.value)}
            >
              {#if optionSnippet}
                {@render optionSnippet(option)}
              {:else if Array.isArray(option.label)}
                <div class="flex justify-between">
                  {#each option.label as label, i}
                    <span>
                      {label}
                    </span>
                  {/each}
                </div>
              {:else}
                {option.label}
              {/if}
            </button>
          {/each}
        </div>
      {/if}

      {#if loading}
        <Spinner size="sm" />
      {:else if value && !dropdown}
        <button type="button" onclick={clearValue} class={classesIcon}>
          <Icon size="md" icon="Close" />
        </button>
      {:else if !value || dropdown}
        <button type="button" onclick={toggleExpanded} class={classesIcon}>
          <Icon size="md" icon="chevronDown" />
        </button>
      {/if}
    </div>
  </div>
</div>
